import React, { useMemo, useRef, useEffect } from 'react';
import { PanelLeft, PanelLeftClose, PanelRightClose } from 'lucide-react';
import type { SidebarTab, CategoryLabel, UnifiedSidebarProps } from './Layout.types';
import {
  entryCategoriesMatch,
  getEntryCategoryColor,
  getEntryCategoryDisplayLabel,
  getPrimaryMenuLabel,
} from './entryCategoryConfig';

export type { UnifiedSidebarProps } from './Layout.types';

function CategoryGroupLabel({ category }: { category: CategoryLabel }) {
  const color = getEntryCategoryColor(category);
  return (
    <div style={{
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '0 12px 0 20px',
      marginBottom: '6px',
    }}>
      <span
        aria-hidden
        style={{
          width: '3px',
          height: '14px',
          borderRadius: '2px',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      <span style={{
        fontSize: '13.75px',
        fontWeight: 700,
        color,
        letterSpacing: '0.04em',
      }}>
        {getEntryCategoryDisplayLabel(category)}
      </span>
    </div>
  );
}

export function UnifiedSidebar({
  categoryOrder = [],
  tabs,
  activeTab,
  collapsible = true,
  collapsed = false,
  onCollapsedChange,
  onTabClick,
  subtitle,
  isMobile = false,
  mobileOpen = false,
  subtitleColor,
  expandedWidthRem = 14,
  headerIcon,
}: UnifiedSidebarProps & { isMobile?: boolean; mobileOpen?: boolean }) {
  const [isCollapsed, setIsCollapsed] = React.useState(collapsed);
  const [isHovered, setIsHovered] = React.useState(false);
  const sidebarRef = useRef<HTMLElement>(null);

  // 모바일에서는 호버 비활성화
  const showExpanded = isMobile ? true : (!isCollapsed || isHovered);

  const handleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    onCollapsedChange?.(newCollapsed);
  };

  const handleTabClick = (tab: SidebarTab) => {
    if (onTabClick) {
      onTabClick(tab);
    } else {
      window.location.href = tab.href;
    }
  };

  // 키보드 네비게이션 지원
  useEffect(() => {
    if (!sidebarRef.current) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isHovered && isCollapsed) {
        setIsHovered(false);
      }
    };

    sidebarRef.current.addEventListener('keydown', handleKeyDown);
    return () => {
      sidebarRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, [isHovered, isCollapsed]);

  const hasCategories = categoryOrder.length > 0 && tabs.some(t => t.labelType?.length);
  const expandedWidth = `${expandedWidthRem}rem`;
  const collapsedWidth = '3.5rem';

  const resolveSubtitleColor = () => {
    if (subtitleColor) return subtitleColor;
    if (subtitle === 'PORTAL SERVICE') return 'hsl(220 80% 45%)';
    if (subtitle?.includes('Portal System')) return 'hsl(200 80% 45%)';
    if (subtitle === 'Tags Visualizer') return 'hsl(25 100% 50%)';
    if (subtitle === 'Tags Editor') return 'hsl(270 70% 45%)';
    return 'hsl(220 9% 20%)';
  };
  const titleColor = resolveSubtitleColor();

  const groupedTabs = useMemo(() => {
    if (!hasCategories) return null;
    const groups = new Map<CategoryLabel, SidebarTab[]>();
    tabs.forEach((tab) => {
      const primaryLabel = getPrimaryMenuLabel(tab.labelType);
      const primaryCategory = primaryLabel
        ? categoryOrder.find((cat) => entryCategoriesMatch(primaryLabel, cat))
        : undefined;
      if (primaryCategory) {
        if (!groups.has(primaryCategory)) groups.set(primaryCategory, []);
        groups.get(primaryCategory)!.push(tab);
      }
    });
    return groups;
  }, [tabs, categoryOrder, hasCategories]);

  // 모바일: 슬라이드 메뉴
  if (isMobile) {
    return (
      <aside
        ref={sidebarRef as React.RefObject<HTMLElement>}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: '280px',
          background: 'hsl(0 0% 100%)',
          zIndex: 40,
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: mobileOpen ? '4px 0 24px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)' : 'none',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          height: '45px',
          minHeight: '45px',
          maxHeight: '45px',
          padding: '0 20px',
          borderBottom: '1px solid hsl(220 13% 91%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'transparent',
        }}>
          {subtitle && (
            <span style={{
              fontSize: '14.4px',
              fontWeight: 600,
              color: titleColor,
              letterSpacing: '-0.01em',
              paddingLeft: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: 0,
            }}>
              {headerIcon}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</span>
            </span>
          )}
        </div>

        {/* 탭 목록 */}
        <nav style={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '12px 12px 16px 12px',
          scrollbarWidth: 'thin',
          scrollbarColor: 'hsl(220 13% 91%) transparent',
        }}>
          {groupedTabs ? (
            categoryOrder.map((category, idx) => {
              const categoryTabs = groupedTabs.get(category);
              if (!categoryTabs || categoryTabs.length === 0) return null;
              return (
                <div key={category} style={{ marginBottom: idx < categoryOrder.length - 1 ? '18px' : '0' }}>
                  <CategoryGroupLabel category={category} />
                  <MobileTabList tabs={categoryTabs} activeTab={activeTab} onTabClick={handleTabClick} />
                </div>
              );
            })
          ) : (
            <MobileTabList tabs={tabs} activeTab={activeTab} onTabClick={handleTabClick} />
          )}
        </nav>
      </aside>
    );
  }

  // 데스크탑: 기존 사이드바
  const sidebarWidth = isCollapsed ? collapsedWidth : expandedWidth;

  return (
    <>
      <div style={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        borderRight: '1px solid hsl(220 13% 91%)',
      }} />

      <aside
        ref={sidebarRef}
        onMouseEnter={() => isCollapsed && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          display: 'flex',
          flexDirection: 'column',
          width: isCollapsed ? collapsedWidth : expandedWidth,
          background: 'hsl(0 0% 100%)',
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 40,
          overflow: 'hidden',
        }}
      >
        {collapsible && (
          <div style={{
            position: 'relative',
            zIndex: 1,
            height: '45px',
            minHeight: '45px',
            maxHeight: '45px',
            padding: '0 12px',
            borderBottom: '1px solid hsl(220 13% 91%)',
            borderRight: '1px solid hsl(220 13% 91%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            backgroundColor: 'transparent',
            boxSizing: 'border-box',
          }}>
            {!isCollapsed && subtitle && (
              <span style={{
                fontSize: '13.5px',
                fontWeight: 600,
                color: titleColor,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                paddingLeft: headerIcon ? '12px' : '22px',
                letterSpacing: '-0.01em',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: 0,
              }}>
                {headerIcon}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</span>
              </span>
            )}
            <button
              onClick={handleCollapse}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCollapse();
                }
              }}
              aria-label={isCollapsed ? '사이드바 펼치기' : '사이드바 접기'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                border: 'none',
                marginLeft: 'auto',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                color: 'hsl(220 9% 46%)',
                transition: 'background-color 0.15s ease, color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'hsl(220 13% 96%)';
                e.currentTarget.style.color = 'hsl(220 9% 20%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'hsl(220 9% 46%)';
              }}
            >
              {isCollapsed ? (
                isHovered ? <PanelRightClose style={{ width: '18px', height: '18px' }} /> : <PanelLeft style={{ width: '18px', height: '18px' }} />
              ) : (
                <PanelLeftClose style={{ width: '18px', height: '18px' }} />
              )}
            </button>
          </div>
        )}

        <nav
          style={{
            position: 'relative',
            zIndex: 1,
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '12px 8px 12px 8px',
            width: showExpanded ? expandedWidth : collapsedWidth,
            backgroundColor: 'transparent',
            transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRight: '1px solid hsl(220 13% 91%)',
            boxShadow: isHovered && isCollapsed ? '4px 0 16px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)' : 'none',
            borderTopRightRadius: isHovered && isCollapsed ? '12px' : 0,
            borderBottomRightRadius: isHovered && isCollapsed ? '12px' : 0,
            scrollbarWidth: 'thin',
            scrollbarColor: 'hsl(220 13% 91%) transparent',
          }}
        >
          {groupedTabs ? (
            categoryOrder.map((category, idx) => {
              const categoryTabs = groupedTabs.get(category);
              if (!categoryTabs || categoryTabs.length === 0) return null;
              return (
                <div key={category} style={{ marginBottom: idx < categoryOrder.length - 1 ? '12px' : '0' }}>
                  {showExpanded ? (
                    <CategoryGroupLabel category={category} />
                  ) : (
                    idx > 0 && <div style={{
                      height: '1px',
                      backgroundColor: 'hsl(220 13% 91%)',
                      margin: '12px 8px',
                    }} />
                  )}
                  <TabList tabs={categoryTabs} showExpanded={showExpanded} />
                </div>
              );
            })
          ) : (
            <TabList tabs={tabs} showExpanded={showExpanded} />
          )}
        </nav>
      </aside>
    </>
  );

  function TabList({ tabs: tabItems, showExpanded: expanded }: { tabs: SidebarTab[]; showExpanded: boolean }) {
    return (
      <ul style={{ display: 'flex', flexDirection: 'column', gap: '2.4px', listStyle: 'none', margin: 0, padding: 0 }}>
        {tabItems.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <li key={tab.id}>
              <button
                onClick={() => handleTabClick(tab)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTabClick(tab);
                  }
                }}
                title={tab.label}
                aria-label={tab.label}
                aria-current={isActive ? 'page' : undefined}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: expanded ? '12px' : '0',
                  padding: expanded ? '9.6px 12px 9.6px 28px' : '8px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '13.2px',
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                  backgroundColor: isActive ? 'hsl(221 83% 53% / 0.12)' : 'transparent',
                  color: '#000000',
                  textAlign: 'left',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  outline: 'none',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'hsl(220 13% 96%)';
                    e.currentTarget.style.color = '#000000';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#000000';
                  }
                }}
                onFocus={(e) => {
                  e.currentTarget.style.outline = '2px solid hsl(221 83% 53%)';
                  e.currentTarget.style.outlineOffset = '2px';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.outline = 'none';
                }}
              >
                <Icon
                  className="shrink-0 object-contain"
                  style={{
                    width: expanded ? '22px' : '24px',
                    height: expanded ? '22px' : '24px',
                    flexShrink: 0,
                    objectFit: 'contain',
                    transition: 'transform 0.2s ease',
                  }}
                />
                {expanded && (
                  <span style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    letterSpacing: '-0.01em',
                    fontSize: '13.2px',
                  }}>
                    {tab.label}
                  </span>
                )}
                {isActive && expanded && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '20px',
                    backgroundColor: 'hsl(221 83% 53%)',
                    borderRadius: '0 2px 2px 0',
                  }} />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  function MobileTabList({
    tabs: tabItems,
    activeTab: currentActiveTab,
    onTabClick: handleClick,
  }: {
    tabs: SidebarTab[];
    activeTab?: string;
    onTabClick: (tab: SidebarTab) => void;
  }) {
    return (
      <ul style={{ display: 'flex', flexDirection: 'column', gap: '2.4px', listStyle: 'none', margin: 0, padding: 0 }}>
        {tabItems.map((tab) => {
          const Icon = tab.icon;
          const isActive = currentActiveTab === tab.id;
          return (
            <li key={tab.id}>
              <button
                onClick={() => handleClick(tab)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick(tab);
                  }
                }}
                aria-label={tab.label}
                aria-current={isActive ? 'page' : undefined}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '9.6px 12px 9.6px 28px',
                  borderRadius: '10px',
                  border: 'none',
                  fontSize: '14.1px',
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                  backgroundColor: isActive ? 'hsl(221 83% 53% / 0.12)' : 'transparent',
                  color: '#000000',
                  textAlign: 'left',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  outline: 'none',
                  letterSpacing: '-0.01em',
                }}
                title={tab.label}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'hsl(220 13% 96%)';
                    e.currentTarget.style.color = '#000000';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#000000';
                  }
                }}
                onFocus={(e) => {
                  e.currentTarget.style.outline = '2px solid hsl(221 83% 53%)';
                  e.currentTarget.style.outlineOffset = '2px';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.outline = 'none';
                }}
              >
                <Icon
                  className="shrink-0 object-contain"
                  style={{
                    width: '22px',
                    height: '22px',
                    flexShrink: 0,
                    objectFit: 'contain',
                    transition: 'transform 0.2s ease',
                  }}
                />
                <span style={{ fontSize: '14.1px' }}>{tab.label}</span>
                {isActive && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '24px',
                    backgroundColor: 'hsl(221 83% 53%)',
                    borderRadius: '0 2px 2px 0',
                  }} />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    );
  }
}
